import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@xanectech/ui/components';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useUnassignedUsersSearch } from '@xanectech/data';
import { UnassignedUserSearchResult } from '@xanectech/types';

interface UserSearchInputProps {
  value: string | null;
  onChange: (
    userId: string | null,
    user?: UnassignedUserSearchResult | null,
  ) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
}

const formatUserLabel = (user: UnassignedUserSearchResult | null) => {
  if (!user) return '';

  const fullName = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();

  if (fullName && user.email) return `${fullName} · ${user.email}`;
  if (fullName) return fullName;
  return user.email || user.user_id;
};

export const UserSearchInput = ({
  value,
  onChange,
  placeholder = 'Search users by name or email...',
  disabled = false,
  required = false,
  name = 'userId',
  id,
}: UserSearchInputProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedUser, setSelectedUser] =
    useState<UnassignedUserSearchResult | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);

    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setDebouncedSearch('');
    }
  }, [open]);

  const {
    data: users,
    isLoading,
    isError,
  } = useUnassignedUsersSearch({
    query: debouncedSearch,
    limit: 15,
    enabled: open,
  });

  useEffect(() => {
    if (!value) {
      setSelectedUser(null);
      return;
    }

    if (users && Array.isArray(users)) {
      const match = (users as UnassignedUserSearchResult[]).find(
        (user: UnassignedUserSearchResult) => user.user_id === value,
      );
      if (match) {
        setSelectedUser(match);
      }
    }
  }, [users, value]);

  const inputId = useMemo(() => id || name, [id, name]);

  const selectedLabel = useMemo(() => {
    if (selectedUser) return formatUserLabel(selectedUser);
    if (value) return value;
    return 'Select a user';
  }, [selectedUser, value]);

  const handleSelect = (user: UnassignedUserSearchResult) => {
    setSelectedUser(user);
    onChange(String(user.user_id), user);
    setOpen(false);
  };

  return (
    <div className='w-full'>
      <input
        type='hidden'
        name={name}
        value={value ?? ''}
        required={required}
        aria-hidden='true'
      />

      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          if (disabled) return;
          setOpen(nextOpen as boolean);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type='button'
            variant='outline'
            role='combobox'
            aria-expanded={open}
            aria-label='Select user'
            id={inputId}
            className='w-full justify-between'
            disabled={disabled}
          >
            <span className='truncate'>{selectedLabel}</span>
            <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
          </Button>
        </PopoverTrigger>
        <PopoverContent className='p-0' align='start'>
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={placeholder}
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>
                {isError
                  ? 'Unable to load users.'
                  : isLoading
                    ? 'Searching users...'
                    : 'No matching users found.'}
              </CommandEmpty>
              <CommandGroup>
                {((users as UnassignedUserSearchResult[]) || []).map(
                  (user: UnassignedUserSearchResult) => {
                    const userLabel = formatUserLabel(user);

                    return (
                      <CommandItem
                        key={user.user_id}
                        value={userLabel}
                        onSelect={() => handleSelect(user)}
                        className='flex items-center justify-between'
                      >
                        <div className='flex flex-col truncate text-left'>
                          <span className='text-sm font-medium leading-tight'>
                            {userLabel}
                          </span>
                          {user.email ? (
                            <span className='text-xs text-muted-foreground'>
                              {user.email}
                            </span>
                          ) : null}
                        </div>
                        {value === user.user_id ? (
                          <Check className='ml-2 h-4 w-4 text-primary' />
                        ) : null}
                      </CommandItem>
                    );
                  },
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
